import { BdArrayUtil } from '@bindoc-projects/util-domain-lib';
import type {
  FilterConditionPluginSpecification,
} from '../filter.plugin';
import {
  FilterConditionPluginImpl
} from '../filter.plugin';
import type {
  BdCondition,
} from '@bindoc-projects/condition-domain-lib';
import {
  BdContentCondition,
} from '@bindoc-projects/condition-domain-lib';

export const AYS_CLINIC_LEVEL_CONDITION_ID = 'clinicLevel';
export const FilterConditionClinicLevelsPluginType: FilterConditionPluginSpecification<
  { includes: string[] },
  FilterConditionClinicLevelsDto,
  AysClinicLevelCondition
> = {
  type: 'clinicLevels',
  category: 'clinic',
  displayName: { de: 'Versorgungslevel' },
  sortingIndex: 8,
  occurrence: 'single',
  fromObject(
    obj: FilterConditionClinicLevelsDto,
  ): FilterConditionPluginImpl<{ includes: string[] }> {
    return new FilterConditionClinicLevelsPlugin(obj.includes ?? []);
  },
  fromCondition(
    jsonObj: AysClinicLevelCondition,
  ): FilterConditionPluginImpl<{ includes: string[] }> {
    return new FilterConditionClinicLevelsPlugin(jsonObj.includes ?? []);
  },
  newInstance(): FilterConditionPluginImpl<{ includes: string[] }> {
    return new FilterConditionClinicLevelsPlugin([]);
  },
};

export class FilterConditionClinicLevelsPlugin extends FilterConditionPluginImpl<
  { includes: string[] },
  FilterConditionClinicLevelsDto
> {
  public static override Specification: FilterConditionPluginSpecification<
    { includes: string[] },
    FilterConditionClinicLevelsDto,
    AysClinicLevelCondition
  > = FilterConditionClinicLevelsPluginType;

  constructor(private includes: string[]) {
    super(FilterConditionClinicLevelsPluginType.type);
  }

  clone(): FilterConditionClinicLevelsPlugin {
    return new FilterConditionClinicLevelsPlugin([...this.includes]);
  }

  override equals(other: FilterConditionClinicLevelsPlugin): boolean {
    return (
      super.equals(other) && BdArrayUtil.equals(this.includes, other.includes)
    );
  }

  /**
   * The inherited `valid()` treats any truthy non-array `toValue()` as valid,
   * and `toValue()` here is the always-truthy `{ includes }` wrapper — so an
   * empty selection passed the form and was saved as `includes: null`
   * (BDA-6550). Mirror {@link validationToText} instead.
   */
  override valid(): boolean {
    return this.includes.length > 0;
  }

  override validationToText(): string {
    if (this.includes.length === 0) {
      return 'Mindestens ein Versorgungslevel auswählen';
    } else {
      return '';
    }
  }

  toCondition(): BdCondition | null {
    return new AysClinicLevelCondition(this.includes, []);
  }

  toJSON(): FilterConditionClinicLevelsDto | null {
    return {
      type: this.type,
      includes: this.includes,
    };
  }

  toText(): string | null {
    return this.includes.length === 1
      ? 'ein Versorgungslevel'
      : `${this.includes.length} Versorgungslevel`;
  }

  toValue(): { includes: string[] } {
    return { includes: this.includes };
  }
}

export class AysClinicLevelCondition extends BdContentCondition<string> {
  constructor(includes: string[], excludes: string[]) {
    super(AYS_CLINIC_LEVEL_CONDITION_ID, includes, excludes);
  }
}

interface FilterConditionClinicLevelsDto {
  type: string;
  includes: string[];
}
